import { useEffect, useState, useRef } from 'react'

interface ApprovalAnimationProps {
  onComplete: () => void
  onUndo?: () => void
  duration?: number // in milliseconds
  showNextPostButton?: boolean
  onNextPostClick?: () => void
}

const ApprovalAnimation = ({
  onComplete,
  onUndo,
  duration = 3000,
  showNextPostButton = false,
  onNextPostClick,
}: ApprovalAnimationProps) => {
  const [progress, setProgress] = useState(100) // Start at 100%
  const [timeLeft, setTimeLeft] = useState(Math.ceil(duration / 1000))
  const onCompleteRef = useRef(onComplete)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Keep the ref updated
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    const startTime = Date.now()

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.max(100 - (elapsed / duration) * 100, 0) // Reverse: start at 100%, go to 0%
      const newTimeLeft = Math.max(Math.ceil((duration - elapsed) / 1000), 0)

      setProgress(newProgress)
      setTimeLeft(newTimeLeft)

      if (elapsed >= duration) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        onCompleteRef.current()
      }
    }, 50) // Update every 50ms for smooth animation

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [duration]) // Only depend on duration

  const handleUndo = () => {
    // Stop the animation timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    // Call the undo callback
    if (onUndo) {
      onUndo()
    }
  }

  return (
    <div className="flex flex-col">
      <div className="w-[360px] h-[56px] flex items-center justify-between pr-[12px] relative whitespace-nowrap rounded-[8px] bg-[#E7F8F2]">
        <div className="flex items-center justify-between w-full z-2">
          {/* Green check icon */}
          <div className="rounded-[8px] rounded-r-none min-w-[56px] h-[56px] text-[40px] text-[#ffffff] flex items-center justify-center bg-[#10B981]">
            <i className="fa-regular fa-check" />
          </div>

          {/* Content area */}
          <div className="flex-1 px-4 flex flex-col justify-center">
            <div className="text-center text-[16px] font-[600] mb-2">
              Approved!
            </div>
          </div>

          {/* Countdown and Undo */}
          <div className="flex items-center gap-[8px]">
            <div className="text-[12px] text-[#808080] font-[500] leading-[16px]">
              {timeLeft}s
            </div>
            <div
              className="text-[12px] text-[#4f31ff] cursor-pointer font-[500]"
              onClick={handleUndo}
            >
              Undo
            </div>
          </div>
        </div>
        {/* Progress bar container - full height of remaining space */}
        <div className="flex-1 flex items-center absolute top-0 left-0 w-full h-full z-1 rounded-[8px] overflow-hidden">
          <div
            className="h-full bg-[#9FE3CD] transition-all duration-75 ease-linear -z-1"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Next pending post button */}
      {showNextPostButton && (
        <button
          className="w-[360px] h-[56px] mt-[16px] flex items-center justify-center gap-[8px] bg-[#F6B324] text-[#ffffff] rounded-[8px] font-[Inter] text-[16px] font-[600] cursor-pointer border-none"
          onClick={onNextPostClick}
        >
          Next pending post
          <i className="fa-regular fa-chevron-right text-[12px]" />
        </button>
      )}
    </div>
  )
}

export default ApprovalAnimation
